import React, { useEffect, useState } from "react";
import SNFDashboard from "./SNFDashboard";
import "./App.css";

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    console.error("UI crash:", error, info);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="app-shell">
          <div className="panel">
            <h2>Something went wrong</h2>
            <p className="hint">
              The app ran into an unexpected error. Please refresh the page.
            </p>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function App() {
  const [globalError, setGlobalError] = useState(null);

  useEffect(() => {
    const reportError = async (payload) => {
      try {
        await fetch("/api/client-error", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
      } catch (err) {
        console.error("Client error report failed:", err);
      }
    };

    const onError = (event) => {
      const payload = {
        message: event.message || "Unknown error",
        source: event.filename || null,
        lineno: event.lineno || null,
        colno: event.colno || null,
        stack: event.error?.stack || null,
        href: window.location.href,
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString(),
      };
      setGlobalError(payload);
      reportError(payload);
    };

    const onRejection = (event) => {
      const reason = event.reason;
      const payload = {
        message: reason?.message || String(reason || "Unhandled rejection"),
        source: null,
        lineno: null,
        colno: null,
        stack: reason?.stack || null,
        href: window.location.href,
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString(),
      };
      setGlobalError(payload);
      reportError(payload);
    };

    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onRejection);
    return () => {
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onRejection);
    };
  }, []);

  return (
    <div className="app-shell">
      {globalError && (
        <div className="global-error-overlay">
          <div className="global-error-card">
            <h2>Something went wrong</h2>
            <p className="hint">
              A client-side error occurred. The details below were logged for debugging.
            </p>
            <div className="error-details">
              <div>
                <strong>Message:</strong> {globalError.message}
              </div>
              {globalError.source && (
                <div>
                  <strong>Source:</strong> {globalError.source}
                </div>
              )}
              {globalError.stack && (
                <pre className="error-stack">{globalError.stack}</pre>
              )}
            </div>
          </div>
        </div>
      )}
      <ErrorBoundary>
        <SNFDashboard />
      </ErrorBoundary>
    </div>
  );
}

export default App;
